import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { WhoPayWhoViewProps } from "@/ui/view-props";
import { PlayerAvatar } from '@/components/PlayerAvatar';

export function WhoPayWhoClassicView({ playerTotals, transactions, targetUserId, isReadOnly, hideHeader }: WhoPayWhoViewProps) {
  return (
    <div className={`${hideHeader ? 'py-0' : 'py-6'} space-y-6`}>
      {!hideHeader && (
        <div className="flex items-center gap-3">
          <Link to={isReadOnly ? `/public/${targetUserId}` : "/"}>
            <button type="button" className="p-2 rounded-lg hover:bg-muted transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Users className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Who Pays Who</h1>
              <p className="text-sm text-muted-foreground">Current unsettled transfers</p>
            </div>
          </div>
        </div>
      )}

      {playerTotals.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <Users className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">No unsettled sessions or expenses.</p>
          <Link to={isReadOnly ? `/public/${targetUserId}` : "/"} className="text-primary text-sm font-medium underline">Go back home</Link>
        </div>
      ) : (
        <>
          {/* Transfer list */}
          {transactions.length > 0 ? (
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                Transfers · {transactions.length} payment{transactions.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {transactions.map((t, i) => (
                  <div key={i} className="bg-card border border-border rounded-lg px-4 py-3 flex items-center gap-2 shadow-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <PlayerAvatar name={t.from} avatarUrl={t.fromAvatarUrl} size={24} />
                      <span className="font-semibold text-sm text-red-500 truncate">{t.from}</span>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <PlayerAvatar name={t.to} avatarUrl={t.toAvatarUrl} size={24} />
                      <span className="font-semibold text-sm text-green-600 truncate">{t.to}</span>
                    </div>
                    <span className="ml-auto font-mono font-bold text-green-600 shrink-0">
                      {formatCurrency(t.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm">
              All balances are even — nothing to transfer!
            </div>
          )}

          {/* Current balances */}
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Current Standings</p>
            <div className="space-y-2">
              {playerTotals.map((pt, i) => (
                <div key={i} className="bg-card border border-border rounded-lg px-4 py-3 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <PlayerAvatar name={pt.name} avatarUrl={pt.avatar_url} size={32} />
                    <div className="min-w-0">
                      <p className="font-semibold">{pt.name}</p>
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-tighter">
                        Spent: {formatCurrency(pt.spent)}
                      </p>
                    </div>
                  </div>
                  <span className={`text-base font-bold font-mono shrink-0 ${pt.net > 0 ? 'text-green-600' : pt.net < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {pt.net >= 0 ? '+' : ''}{formatCurrency(pt.net)}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">* Showing balances from all unsettled sessions and expenses.</p>
          </div>
        </>
      )}
    </div>
  );
}
